import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useState } from 'react'

import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { conditionalInvoke } from 'aem-libs-ts/src/utils/common/conditionalInvoke'

import { HandlersType, TrackingConfig } from './types'


type ValueOptions = {
  pattern?: RegExp
}

export function useFieldValue<TElement extends HTMLInputElement | HTMLTextAreaElement, TValue extends string | number>(
  value: TValue,
  handlers: HandlersType<TValue, TElement>,
  tracking: TrackingConfig,
  options?: ValueOptions
) {
  const [currentValue, setCurrentValue] = useState(value ?? '')
  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    id: tracking.id,
    description: tracking.description
  })

  useEffect(() => {
    if (value !== currentValue) {
      setCurrentValue(value)
    }
  }, [value])

  const handleChange = (e: ChangeEvent<TElement>) => {
    if (e.target.value?.length > 0 && options?.pattern && !options.pattern.test(e.target.value)) {
      return
    }
    conditionalInvoke(handlers.onChange, [e])
    setCurrentValue(e.target.value as TValue)
  }

  const handleBlur = (e: FocusEvent<TElement>) => {
    conditionalInvoke(handlers.onChangeComplete, [currentValue])
    conditionalInvoke(handlers.onBlur, [e])
    trackInputChange(currentValue)
  }

  const handleKeyPress = (e: KeyboardEvent<TElement>) => {
    if (e.key === 'Enter') {
      conditionalInvoke(handlers.onChangeComplete, [currentValue])
    }
    conditionalInvoke(handlers.onKeyPress, [e])
  }

  return {
    currentValue,
    handleChange,
    handleBlur,
    handleKeyPress
  }
}
